import React, { useCallback, useMemo, useState } from 'react'
import { Organization } from '../../../shared/src/data/roleHierarchy/organization'
import {
  Box,
  Button,
  createStyles,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { RoleHierarchy } from '../../../shared/src/data/roleHierarchy/roleHierarchy'
import { useRoleHierarchyManager } from './queries/roleHierarchyManagement'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  }),
)

type RoleHierarchyCreatorProps = { roleHierarchies: RoleHierarchy[] }

export function RoleHierarchyCreator({
  roleHierarchies,
}: RoleHierarchyCreatorProps): JSX.Element {
  const classes = useStyles()
  const {
    createRoleHierarchyMutation: { mutateAsync: createRoleHierarchy },
    createBranchMutation: { mutateAsync: createBranch },
  } = useRoleHierarchyManager()
  const [resultMessage, setResultMessage] = useState<{
    message: string
    type: 'error' | 'success'
  } | null>(null)
  const [useExistingOrganization, setUseExistingOrganization] =
    useState<boolean>(false)
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>()
  const [newOrganizationName, setNewOrganizationName] = useState<string>()
  const [newBranchName, setNewBranchName] = useState<string>()

  const organizations = useMemo<Organization[]>(() => {
    const nonUniqueOrganizations =
      roleHierarchies?.map((a) => a.organization) || []
    return nonUniqueOrganizations.filter(
      (a1, i) =>
        nonUniqueOrganizations.findIndex((a2) => a1.id === a2.id) === i,
    )
  }, [roleHierarchies])

  const setSelectedOrganizationByOrganizationId = useCallback(
    (id: string) => {
      setSelectedOrganization(
        roleHierarchies.find((a) => a.organization.id === id)!.organization,
      )
    },
    [roleHierarchies],
  )

  const handleButtonClicked = useCallback(async () => {
    setResultMessage(null)
    try {
      if (useExistingOrganization) {
        await createBranch({
          organization: selectedOrganization!,
          branchName: newBranchName!,
        })
      } else {
        await createRoleHierarchy({
          organizationName: newOrganizationName!,
          branchName: newBranchName!,
        })
      }
      setResultMessage({
        message: 'Create role hierarchy successfully.',
        type: 'success',
      })
    } catch (error) {
      setResultMessage({
        message: 'Failed creating role hierarchy.',
        type: 'error',
      })
    }
  }, [
    createRoleHierarchy,
    useExistingOrganization,
    selectedOrganization,
    newOrganizationName,
    newBranchName,
    createBranch,
  ])

  return (
    <Box className={classes.root}>
      <Box>
        <Box display='flex' alignItems='center'>
          <Typography>Use existing organization: </Typography>
          <Switch
            checked={useExistingOrganization}
            onChange={(e, c) => setUseExistingOrganization(c)}
          />
        </Box>
        {useExistingOrganization ? (
          <FormControl fullWidth required color='secondary'>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrganization?.id || ''}
              fullWidth
              onChange={(e) =>
                setSelectedOrganizationByOrganizationId(
                  e.target.value as string,
                )
              }
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label='Organization'
            color='secondary'
            required
            value={newOrganizationName}
            fullWidth
            onChange={(v) => setNewOrganizationName(v.target.value)}
          />
        )}
      </Box>
      <TextField
        label='Branch'
        color='secondary'
        required
        value={newBranchName}
        onChange={(v) => setNewBranchName(v.target.value)}
      />
      <Button
        style={{ marginTop: '16px' }}
        variant='contained'
        color='primary'
        disabled={
          !newBranchName ||
          (useExistingOrganization && !selectedOrganization) ||
          (!useExistingOrganization && !newOrganizationName)
        }
        onClick={handleButtonClicked}
      >
        Create
      </Button>
      {!!resultMessage && (
        <Typography
          color={resultMessage!.type === 'error' ? 'error' : 'textPrimary'}
        >
          {resultMessage.message}
        </Typography>
      )}
    </Box>
  )
}
